import React, { useEffect, useState } from "react";
import { UserCircle, Loader2, Pencil, Save, ShieldCheck, Mail, Smartphone, Calendar, MapPin, Phone, BadgeCheck, Bell, KeyRound, Send, Download, BellRing, Share } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { pushSupported, pushStatus, enablePush } from "../../lib/push";
import { saveActivities } from "../../components/dashboard/ActivitySwitch";

const fmtDate = (s) => {
  if (!s) return "—";
  try { return new Date(s).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" }); }
  catch { return s; }
};

const inp = "w-full px-4 py-2.5 rounded-xl border border-brand-soft bg-white outline-none focus:border-brand-lime text-sm text-brand-greenDeep";

const COUNTRIES = [
  "France", "Belgique", "Suisse", "Luxembourg", "Monaco", "Canada", "Allemagne", "Espagne",
  "Italie", "Portugal", "Pays-Bas", "Royaume-Uni", "Irlande", "Autriche", "Danemark", "Suède",
  "Norvège", "Finlande", "Pologne", "Grèce", "Roumanie", "République tchèque", "Hongrie",
  "États-Unis", "Maroc", "Algérie", "Tunisie", "Sénégal", "Côte d'Ivoire", "Cameroun",
  "Île Maurice", "Madagascar", "Liban", "Autre",
];

const PUSH_EVENTS = [
  ["message", "Nouveau message", "Message reçu dans la messagerie"],
  ["rent_received", "Loyer reçu", "Paiement de loyer enregistré"],
  ["unpaid", "Loyer impayé", "Retard ou impayé détecté"],
  ["reservation", "Location saisonnière", "Réservation, annulation, chevauchement"],
];

function MesActivites() {
  const { user, setUser } = useAuth();
  if (!["bailleur", "agent", "gestionnaire", "courtier", "admin"].includes(user?.role)) return null;
  const acts = user?.activities || { longue: true, saisonnier: false };
  const toggle = async (k) => {
    const next = { ...acts, [k]: !acts[k] };
    if (!next.longue && !next.saisonnier) { toast.error("Gardez au moins une activité active"); return; }
    try { await saveActivities(next, user, setUser); toast.success("Activités mises à jour"); } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const rows = [["longue", "Location longue durée", "Biens loués nus ou meublés à l'année : baux, quittances, locataires"], ["saisonnier", "Location saisonnière", "Meublés de tourisme : calendrier, synchro Airbnb / Booking / Abritel, réservations, conformité"]];
  return (
    <div className="bg-white rounded-3xl border border-brand-soft p-6" data-testid="activities-section">
      <h2 className="font-display text-xl font-semibold text-brand-greenDeep">Mes activités</h2>
      <p className="text-xs text-brand-green/50 mb-4">Affichez uniquement les modules dont vous avez besoin. Raccourci : la pastille en haut du menu.</p>
      <div className="grid sm:grid-cols-2 gap-3">
        {rows.map(([k, label, desc]) => (
          <button key={k} onClick={() => toggle(k)} className={`flex items-center gap-3 px-4 py-3 rounded-2xl border text-left transition-colors ${acts[k] ? "border-emerald-400 bg-emerald-50" : "border-brand-soft hover:bg-brand-softer"}`} data-testid={`activity-toggle-${k}`}>
            <div className="flex-1"><p className="font-semibold text-brand-greenDeep text-sm">{label}</p><p className="text-xs text-brand-green/50">{desc}</p></div>
            <span className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${acts[k] ? "bg-emerald-500" : "bg-brand-soft"}`}><span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${acts[k] ? "left-[22px]" : "left-0.5"}`} /></span>
          </button>
        ))}
      </div>
    </div>
  );
}

export default function MonCompte() {
  const { setUser } = useAuth();
  const [acc, setAcc] = useState(null);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [twofaBusy, setTwofaBusy] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [pushState, setPushState] = useState("off");
  const [pushBusy, setPushBusy] = useState(false);
  const isIOS = typeof navigator !== "undefined" && /iphone|ipad|ipod/i.test(navigator.userAgent);

  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setInstallPrompt(e); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    const standalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
    setInstalled(standalone);
    pushStatus().then(setPushState).catch(() => {});
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  const doInstall = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    if (outcome === "accepted") { setInstalled(true); toast.success("Application installée"); }
    setInstallPrompt(null);
  };

  const doEnablePush = async () => {
    setPushBusy(true);
    try { await enablePush(); setPushState("on"); toast.success("Notifications activées sur cet appareil"); }
    catch (e) { toast.error(e?.message || "Activation impossible"); }
    finally { setPushBusy(false); }
  };

  const load = async () => {
    try { const r = await api.get("/auth/account"); setAcc(r.data); }
    catch { toast.error("Chargement du compte impossible"); }
  };
  useEffect(() => { load(); }, []);

  const openEdit = () => {
    const parts = (acc.name || "").trim().split(" ");
    const fp = parts[0] || "";
    const rest = parts.slice(1).join(" ");
    setForm({
      prenom: acc.prenom || fp || "", nom: acc.nom || rest || "", address: acc.address || "", address2: acc.address2 || "",
      postal_code: acc.postal_code || "", city: acc.city || "", country: acc.country || "France", mobile: acc.mobile || "",
    });
    setEdit(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put("/auth/profile", form);
      setAcc(r.data);
      const me = await api.get("/auth/me"); setUser(me.data);
      toast.success("Informations mises à jour");
      setEdit(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const toggle2fa = async (method) => {
    const willEnable = !(acc.two_factor_enabled && method === acc.two_factor_method);
    if (willEnable && method === "sms" && !(acc.mobile || "").trim()) {
      toast.error("Renseignez d'abord votre téléphone mobile (format +33…) puis réessayez.");
      return;
    }
    setTwofaBusy(true);
    try {
      const r = await api.put("/auth/2fa", { enabled: willEnable, method, phone: (acc.mobile || "").trim() });
      setAcc({ ...acc, two_factor_enabled: r.data.two_factor_enabled, two_factor_method: r.data.two_factor_method });
      toast.success(r.data.two_factor_enabled ? "Sécurisation activée" : "Sécurisation désactivée");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setTwofaBusy(false); }
  };

  const toggleMsgAlerts = async () => {
    const next = !acc.email_message_alerts;
    setAcc({ ...acc, email_message_alerts: next });
    try {
      await api.put("/auth/notification-prefs", { email_message_alerts: next });
      toast.success(next ? "Alertes e-mail activées" : "Alertes e-mail désactivées");
    } catch (e) { setAcc({ ...acc, email_message_alerts: !next }); toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const toggleTenantEmails = async () => {
    const next = !acc.tenant_emails_enabled;
    setAcc({ ...acc, tenant_emails_enabled: next });
    try {
      await api.put("/auth/notification-prefs", { tenant_emails_enabled: next });
      toast.success(next ? "Envoi d'e-mails aux locataires activé" : "Envoi d'e-mails aux locataires désactivé");
    } catch (e) { setAcc({ ...acc, tenant_emails_enabled: !next }); toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const togglePushPref = async (kind) => {
    const prevPrefs = acc.push_prefs || {};
    const next = !(prevPrefs[kind] ?? true);
    setAcc({ ...acc, push_prefs: { ...prevPrefs, [kind]: next } });
    try {
      await api.put("/auth/notification-prefs", { push_prefs: { [kind]: next } });
      toast.success(next ? "Notification push activée" : "Notification push désactivée");
    } catch (e) { setAcc({ ...acc, push_prefs: prevPrefs }); toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  if (!acc) return <div className="py-10 text-center text-brand-green/50"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;

  const fullAddr = [acc.address, acc.address2, [acc.postal_code, acc.city].filter(Boolean).join(" "), acc.country].filter(Boolean).join(", ");

  return (
    <div className="space-y-6" data-testid="mon-compte">
      {/* Identité */}
      <div className="bg-white rounded-3xl border border-brand-soft p-6">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-brand-lime/30 text-brand-greenDeep flex items-center justify-center"><UserCircle className="w-5 h-5" /></div>
            <div>
              <h2 className="font-display text-2xl font-semibold text-brand-greenDeep">Mon compte</h2>
              <p className="text-sm text-brand-green/55">Vos informations d'identification</p>
            </div>
          </div>
          {!edit ? (
            <button onClick={openEdit} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-brand-soft hover:bg-brand-softer text-brand-greenDeep text-sm font-semibold" data-testid="edit-account-btn"><Pencil className="w-4 h-4" /> Modifier</button>
          ) : (
            <button onClick={save} disabled={busy} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-greenDeep hover:bg-brand-green text-white text-sm font-semibold disabled:opacity-60" data-testid="save-account-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Enregistrer</button>
          )}
        </div>

        {!edit ? (
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4 mt-6">
            <Info label="Nom et prénom" value={acc.name || [acc.prenom, acc.nom].filter(Boolean).join(" ") || "—"} icon={BadgeCheck} testid="acc-name" />
            <Info label="Email" value={acc.email || "—"} icon={Mail} testid="acc-email" />
            <Info label="Adresse complète" value={fullAddr || "—"} icon={MapPin} testid="acc-address" />
            <Info label="Téléphone mobile" value={acc.mobile || "—"} icon={Phone} testid="acc-mobile" />
            <Info label="Formule en cours" value={acc.plan || "—"} icon={ShieldCheck} testid="acc-plan" />
            <Info label="Renouvellement de la formule" value={fmtDate(acc.renewal_date)} icon={Calendar} testid="acc-renewal" />
            <Info label="Création de l'identifiant" value={fmtDate(acc.created_at)} icon={Calendar} testid="acc-created" />
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4 mt-6">
            <Field label="Prénom"><input value={form.prenom} onChange={(e) => setForm({ ...form, prenom: e.target.value })} className={inp} data-testid="edit-prenom" placeholder="Votre prénom" /></Field>
            <Field label="Nom"><input value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} className={inp} data-testid="edit-nom" placeholder="Votre nom" /></Field>
            <Field label="Email (non modifiable)"><input value={acc.email || ""} readOnly disabled className={`${inp} bg-brand-softer text-brand-green/50 cursor-not-allowed`} data-testid="edit-email" /></Field>
            <Field label="Téléphone mobile"><input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} className={inp} data-testid="edit-mobile" placeholder="+33 6 ..." /></Field>
            <Field label="Adresse"><input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className={inp} data-testid="edit-address" placeholder="Numéro et rue" /></Field>
            <Field label="Complément d'adresse"><input value={form.address2} onChange={(e) => setForm({ ...form, address2: e.target.value })} className={inp} placeholder="Bâtiment, étage… (facultatif)" /></Field>
            <Field label="Code postal"><input value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} className={inp} placeholder="Code postal" /></Field>
            <Field label="Ville"><input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className={inp} placeholder="Ville" /></Field>
            <Field label="Pays">
              <select value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className={inp} data-testid="edit-country">
                {(COUNTRIES.includes(form.country) ? COUNTRIES : [form.country, ...COUNTRIES]).map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </Field>
          </div>
        )}
      </div>

      {/* Sécurisation de la connexion (2FA) */}
      <div className="bg-white rounded-3xl border border-brand-soft p-6" data-testid="security-section">
        <div className="flex items-center gap-3">
          <div className={`w-11 h-11 rounded-2xl flex items-center justify-center ${acc.two_factor_enabled ? "bg-emerald-100 text-emerald-600" : "bg-brand-softer text-brand-green/60"}`}><ShieldCheck className="w-5 h-5" /></div>
          <div>
            <h3 className="font-display text-xl font-semibold text-brand-greenDeep">Sécurisation de la connexion</h3>
            <p className="text-sm text-brand-green/55">{acc.two_factor_enabled ? `Activée — code par ${acc.two_factor_method === "sms" ? "SMS" : "email"} à chaque connexion` : "Ajoutez un code de vérification à chaque ouverture de session"}</p>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-3 mt-5">
          {[["email", "Par email", Mail], ["sms", "Par SMS (mobile)", Smartphone]].map(([m, label, Icon]) => {
            const active = acc.two_factor_enabled && acc.two_factor_method === m;
            return (
              <button key={m} onClick={() => toggle2fa(m)} disabled={twofaBusy} className={`flex items-center gap-3 px-4 py-3 rounded-2xl border text-left transition-colors disabled:opacity-60 ${active ? "border-emerald-400 bg-emerald-50" : "border-brand-soft hover:bg-brand-softer"}`} data-testid={`twofa-${m}`}>
                <Icon className={`w-5 h-5 ${active ? "text-emerald-600" : "text-brand-green/60"}`} />
                <div className="flex-1">
                  <p className="font-semibold text-brand-greenDeep text-sm">{label}</p>
                  <p className="text-xs text-brand-green/50">{active ? "Activé — cliquez pour désactiver" : "Cliquez pour activer"}</p>
                </div>
                {active && <BadgeCheck className="w-5 h-5 text-emerald-600" />}
              </button>
            );
          })}
        </div>
        <p className="text-xs text-brand-green/40 mt-3">Le code par SMS utilise votre téléphone mobile ci-dessus. Selon le réglage de l'administrateur, le code est soit affiché à l'écran (mode démo), soit envoyé par email/SMS (mode production).</p>
      </div>

      {/* Notifications par e-mail */}
      <div className="bg-white rounded-3xl border border-brand-soft p-6" data-testid="notif-prefs-section">
        <div className="flex items-center gap-3">
          <div className={`w-11 h-11 rounded-2xl flex items-center justify-center ${acc.email_message_alerts ? "bg-emerald-100 text-emerald-600" : "bg-brand-softer text-brand-green/60"}`}><Bell className="w-5 h-5" /></div>
          <div>
            <h3 className="font-display text-xl font-semibold text-brand-greenDeep">Notifications par e-mail</h3>
            <p className="text-sm text-brand-green/55">Recevez un e-mail lorsqu'un nouveau message arrive dans votre messagerie.</p>
          </div>
        </div>
        <button onClick={toggleMsgAlerts} className={`w-full mt-5 flex items-center gap-3 px-4 py-3 rounded-2xl border text-left transition-colors ${acc.email_message_alerts ? "border-emerald-400 bg-emerald-50" : "border-brand-soft hover:bg-brand-softer"}`} data-testid="toggle-msg-alerts">
          <Mail className={`w-5 h-5 ${acc.email_message_alerts ? "text-emerald-600" : "text-brand-green/60"}`} />
          <div className="flex-1">
            <p className="font-semibold text-brand-greenDeep text-sm">Alertes e-mail de nouveaux messages</p>
            <p className="text-xs text-brand-green/50">{acc.email_message_alerts ? "Activées — cliquez pour désactiver" : "Désactivées — cliquez pour activer"}</p>
          </div>
          <span className={`relative w-11 h-6 rounded-full transition-colors ${acc.email_message_alerts ? "bg-emerald-500" : "bg-brand-soft"}`}><span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${acc.email_message_alerts ? "left-[22px]" : "left-0.5"}`} /></span>
        </button>
      </div>

      {/* Envoi d'e-mails aux locataires (bailleur) */}
      {acc.role === "bailleur" && (
      <div className="bg-white rounded-3xl border border-brand-soft p-6" data-testid="tenant-emails-section">
        <div className="flex items-center gap-3">
          <div className={`w-11 h-11 rounded-2xl flex items-center justify-center ${acc.tenant_emails_enabled ? "bg-emerald-100 text-emerald-600" : "bg-brand-softer text-brand-green/60"}`}><Send className="w-5 h-5" /></div>
          <div>
            <h3 className="font-display text-xl font-semibold text-brand-greenDeep">Envoi d'e-mails aux locataires</h3>
            <p className="text-sm text-brand-green/55">Contrôlez l'envoi automatique des quittances et des relances de loyer à vos locataires.</p>
          </div>
        </div>
        <button onClick={toggleTenantEmails} className={`w-full mt-5 flex items-center gap-3 px-4 py-3 rounded-2xl border text-left transition-colors ${acc.tenant_emails_enabled ? "border-emerald-400 bg-emerald-50" : "border-brand-soft hover:bg-brand-softer"}`} data-testid="toggle-tenant-emails">
          <Mail className={`w-5 h-5 ${acc.tenant_emails_enabled ? "text-emerald-600" : "text-brand-green/60"}`} />
          <div className="flex-1">
            <p className="font-semibold text-brand-greenDeep text-sm">Envoi des quittances et relances par e-mail</p>
            <p className="text-xs text-brand-green/50">{acc.tenant_emails_enabled ? "Activé — les quittances et relances sont envoyées aux locataires" : "Désactivé — aucune quittance ni relance n'est envoyée aux locataires (les PDF restent générés)"}</p>
          </div>
          <span className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${acc.tenant_emails_enabled ? "bg-emerald-500" : "bg-brand-soft"}`}><span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${acc.tenant_emails_enabled ? "left-[22px]" : "left-0.5"}`} /></span>
        </button>
      </div>
      )}

      {/* Application mobile (PWA) */}
      <div className="bg-white rounded-3xl border border-brand-soft p-6" data-testid="pwa-section">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl flex items-center justify-center bg-brand-greenDeep text-white"><Smartphone className="w-5 h-5" /></div>
          <div>
            <h3 className="font-display text-xl font-semibold text-brand-greenDeep">Application mobile</h3>
            <p className="text-sm text-brand-green/55">Installez 360immoinvest sur votre écran d'accueil et recevez des notifications.</p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-3 mt-5">
          <div className="rounded-2xl border border-brand-soft p-4">
            <p className="font-semibold text-brand-greenDeep text-sm flex items-center gap-2"><Download className="w-4 h-4" /> Installer l'application</p>
            {installed ? (
              <p className="text-xs text-brand-emerald mt-2 font-medium flex items-center gap-1.5"><BadgeCheck className="w-4 h-4" /> Application déjà installée</p>
            ) : installPrompt ? (
              <button onClick={doInstall} className="mt-3 w-full py-2.5 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold text-sm transition-colors" data-testid="pwa-install-btn">Installer maintenant</button>
            ) : isIOS ? (
              <p className="text-xs text-brand-green/55 mt-2 flex items-start gap-1.5"><Share className="w-4 h-4 shrink-0 mt-0.5" /> Sur iPhone/iPad : touchez <b>Partager</b> puis <b>« Sur l'écran d'accueil »</b>.</p>
            ) : (
              <p className="text-xs text-brand-green/55 mt-2">Utilisez le menu de votre navigateur → <b>« Installer l'application »</b> / <b>« Ajouter à l'écran d'accueil »</b>.</p>
            )}
          </div>

          <div className="rounded-2xl border border-brand-soft p-4">
            <p className="font-semibold text-brand-greenDeep text-sm flex items-center gap-2"><BellRing className="w-4 h-4" /> Notifications push</p>
            {pushState === "unsupported" ? (
              <p className="text-xs text-brand-green/55 mt-2">Non supporté sur ce navigateur. Sur iPhone, installez d'abord l'application (iOS 16.4+).</p>
            ) : pushState === "on" ? (
              <p className="text-xs text-brand-emerald mt-2 font-medium flex items-center gap-1.5"><BadgeCheck className="w-4 h-4" /> Notifications activées sur cet appareil</p>
            ) : pushState === "denied" ? (
              <p className="text-xs text-brand-green/55 mt-2">Autorisation bloquée. Réactivez les notifications dans les réglages de votre navigateur.</p>
            ) : (
              <button onClick={doEnablePush} disabled={pushBusy} className="mt-3 w-full py-2.5 rounded-full bg-brand-greenDeep hover:bg-brand-green text-white font-semibold text-sm transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2" data-testid="pwa-push-btn">
                {pushBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <BellRing className="w-4 h-4" />} Activer les notifications
              </button>
            )}
          </div>
        </div>

        <div className="mt-5" data-testid="push-prefs-section">
          <p className="text-sm font-semibold text-brand-greenDeep">Événements notifiés</p>
          <p className="text-xs text-brand-green/50 mb-3">Choisissez les événements qui déclenchent une notification push sur vos appareils.</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {PUSH_EVENTS.map(([kind, label, desc]) => {
              const on = (acc.push_prefs || {})[kind] ?? true;
              return (
                <button key={kind} onClick={() => togglePushPref(kind)} className={`flex items-center gap-3 px-4 py-3 rounded-2xl border text-left transition-colors ${on ? "border-emerald-400 bg-emerald-50" : "border-brand-soft hover:bg-brand-softer"}`} data-testid={`push-pref-${kind}`}>
                  <div className="flex-1">
                    <p className="font-semibold text-brand-greenDeep text-sm">{label}</p>
                    <p className="text-xs text-brand-green/50">{desc}</p>
                  </div>
                  <span className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${on ? "bg-emerald-500" : "bg-brand-soft"}`}><span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${on ? "left-[22px]" : "left-0.5"}`} /></span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <MesActivites />

      {/* Changer mon mot de passe */}
      <ChangePassword />
    </div>
  );
}

function ChangePassword() {
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (nw.length < 8) { toast.error("Le nouveau mot de passe doit contenir au moins 8 caractères"); return; }
    if (nw !== confirm) { toast.error("La confirmation ne correspond pas"); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: nw });
      toast.success("Mot de passe mis à jour");
      setCur(""); setNw(""); setConfirm("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };
  return (
    <div className="bg-white rounded-3xl border border-brand-soft p-6" data-testid="change-password-section">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-2xl bg-brand-lime/30 text-brand-greenDeep flex items-center justify-center"><KeyRound className="w-5 h-5" /></div>
        <div>
          <h3 className="font-display text-xl font-semibold text-brand-greenDeep">Changer mon mot de passe</h3>
          <p className="text-sm text-brand-green/55">Saisissez votre mot de passe actuel puis le nouveau (8 caractères minimum).</p>
        </div>
      </div>
      <div className="grid sm:grid-cols-3 gap-3 mt-5">
        <input type="password" value={cur} onChange={(e) => setCur(e.target.value)} placeholder="Mot de passe actuel" autoComplete="current-password" className={inp} data-testid="cp-current" />
        <input type="password" value={nw} onChange={(e) => setNw(e.target.value)} placeholder="Nouveau mot de passe" autoComplete="new-password" className={inp} data-testid="cp-new" />
        <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Confirmer le nouveau" autoComplete="new-password" className={inp} data-testid="cp-confirm" />
      </div>
      <button onClick={submit} disabled={busy || !cur || !nw || !confirm} className="mt-4 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-brand-greenDeep hover:bg-brand-green text-white text-sm font-semibold disabled:opacity-60" data-testid="cp-submit">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />} Mettre à jour
      </button>
    </div>
  );
}

const Info = ({ label, value, icon: Icon, testid }) => (
  <div className="flex items-start gap-3">
    <Icon className="w-4 h-4 text-brand-green/40 mt-1 flex-shrink-0" />
    <div>
      <p className="text-xs text-brand-green/45">{label}</p>
      <p className="text-sm font-medium text-brand-greenDeep" data-testid={testid}>{value}</p>
    </div>
  </div>
);

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-sm font-medium text-brand-green/70 mb-1.5 block">{label}</span>
    {children}
  </label>
);
